<template>
    <div class="bg-white w-full">
        <draggable
            v-model="local_folders"
            :disabled="read_only"
            :animation="150"
            ghost-class="sortable-ghost"
            chosen-class="sortable-chosen"
            drag-class="sortable-drag"
            handle=".folder-drag-header"
            :force-fallback="true"
            :fallback-class="'sortable-fallback'"
            @start="onFolderDragStart"
            @end="onFolderDragEnd"
            class="space-y-0"
        >
            <div v-for="(folder, index) in local_folders" :key="folder.id" 
                 :class="index < local_folders.length - 1 ? 'border-b border-gray-300' : ''"
                 :data-folder-id="folder.id"
            >
                <document-folder
                    :folder="folder"
                    :property_id="property.id"
                    :documents="getDocumentsForFolder(folder.id)"
                    :templates="getTemplatesForFolder(folder.id)"
                    :has_active_docusign_user="has_active_docusign_user"
                    :store_route="store_route"
                    :folder_to_expand="folder_to_expand"
                    :selection_mode="selection_mode"
                    :selected_ids="selected_ids"
                    @expanded_folder="expandedFolder"
                    @documentUploaded="documentUploaded"
                    @updatedDocument="updatedDocument"
                    @documentEdited="documentEdited"
                    @documentDeleted="documentDeleted"
                    @editFolder="editFolder"
                    @selection-changed="handleSelectionChanged"
                />
            </div>
        </draggable>

        <document-folder-edit-modal
            :show="show_edit_folder_modal"
            :folder="selected_folder"
            :property_id="property.id"
            @closed="editFolderModalClosed"
            @updatedFolder="updateFolder"
            @deletedFolder="deleteFolder"
        />
    </div>
</template>

<script>
import DocumentFolder from './document-folder.vue';
import DocumentFolderEditModal from './document-folder-edit-modal.vue';
import { FolderPlusIcon, FolderIcon } from 'vue-feather-icons';
import draggable from 'vuedraggable';
import { orderBy } from 'lodash';

export default {
    components: {
        DocumentFolder,
        DocumentFolderEditModal,
        draggable,
        FolderPlusIcon,
        FolderIcon,
    },

    props: {
        folders: {},
        property: {},
        has_filter: { default: false },
        documents: {},
        templates: {},
        read_only: { default: false },
        has_active_docusign_user: { default: false },
        store_route: {},
        folder_to_expand: {},
        selection_mode: { default: false },
        selected_ids: { default: () => [] },
    },

    data() {
        return {
            documents_proxy: this.documents || [],
            selected_folder: null,
            show_edit_folder_modal: false,
            templates_proxy: this.templates || [],
            local_folders: this.folders || [],
            is_dragging: false,
        }
    },

    methods: {
        updatedDocument(document) {
            this.$emit('updated_document', document);
        },
        expandedFolder(folder) {
            this.$emit('expanded_folder', folder);
        },
        getDocumentsForFolder(folder_id) {
            if (!this.documents_proxy || !Array.isArray(this.documents_proxy)) {
                return [];
            }
            const documents = this.documents_proxy.filter(document => document.folder_id == folder_id);
            return orderBy(
                documents,
                [doc => (doc.display_filename || doc.filename || '').toLowerCase()],
                ['asc']
            );
        },
        getTemplatesForFolder(folder_id) {
            if (!this.templates_proxy || !Array.isArray(this.templates_proxy)) {
                return [];
            }
            // Find the folder to get its default_folder_id
            const folder = this.folders.find(f => f.id == folder_id);
            if (!folder || !folder.default_folder_id) {
                return [];
            }
            // Filter templates by the folder's default_folder_id
            return this.templates_proxy.filter(template => template.folder_id == folder.default_folder_id);
        },
        documentEdited(updatedDocument) {
            this.$emit('documentEdited', updatedDocument);
        },
        documentDeleted(id) {
            this.$emit('documentDeleted', id);
        },
        documentUploaded(document) {
            this.$emit('documentUploaded', document);
        },
        editFolder(folder) {
            this.selected_folder = folder;
            this.show_edit_folder_modal = true;
        },
        editFolderModalClosed() {
            this.selected_folder = null;
            this.show_edit_folder_modal = false;
        },
        updateFolder(folder) {
            this.$emit('updated_folder', folder);
        },
        deleteFolder(folder) {
            this.$emit('deleted_folder', folder);
        },
        onFolderDragStart(event) {
            this.is_dragging = true;

            // Set cursor to move on the document body
            document.body.style.cursor = 'move';

            // Only try to set drag image if dataTransfer is available
            if (event.dataTransfer && event.dataTransfer.setDragImage) {
                // Create a custom drag image to replace the default circular icon
                const dragImage = document.createElement('div');
                dragImage.style.cssText = `
                    position: absolute;
                    top: -1000px;
                    left: -1000px;
                    width: 1px;
                    height: 1px;
                    background: transparent;
                    border: none;
                    opacity: 0;
                `;
                document.body.appendChild(dragImage);
                event.dataTransfer.setDragImage(dragImage, 0, 0);

                // Clean up the drag image after a short delay
                setTimeout(() => {
                    if (document.body.contains(dragImage)) {
                        document.body.removeChild(dragImage);
                    }
                }, 100);
            }
        },
        onFolderDragEnd() {
            this.is_dragging = false;

            // Reset cursor to default
            document.body.style.cursor = 'default';

            // Extract folder IDs in the new order
            const ordered_ids = this.local_folders.map(folder => folder.id);
            this.updateFolderOrder(ordered_ids);
        },
        updateFolderOrder(ordered_ids) {
            axios.post(`/properties/${this.property.id}/document-folders/reorder`, {
                folder_ids: ordered_ids
            }).then(response => {
                this.$emit('folders-reordered', response.data);
            }).catch(error => {
                console.error('Error reordering folders:', error);
            });
        },
        handleSelectionChanged(selected_ids) {
            this.$emit('selection-changed', selected_ids);
        },
    },

    computed: {
        is_buyer_side() {
            return this.property.deal.our_purchase;
        },
        is_seller_side() {
            return this.property.deal.our_sale;
        },
        filtered_folders() {
            if (!this.local_folders || !Array.isArray(this.local_folders)) {
                return [];
            }
            return this.local_folders.filter(folder => {
                return (this.is_buyer_side && folder.is_buyer_side)
                    || (this.is_seller_side && folder.is_seller_side)
                    || (! folder.is_buyer_side && ! folder.is_seller_side);
            });
        },
    },

    watch: {
        documents: {
            handler(newDocuments) {
                // Update local documents_proxy when parent component refreshes documents
                this.documents_proxy = newDocuments || [];
            },
            deep: true
        },
        templates: {
            handler(newTemplates) {
                // Update local templates_proxy when parent component refreshes templates
                this.templates_proxy = newTemplates || [];
            },
            deep: true
        },
        folders: {
            handler(newFolders) {
                // Update local_folders when parent component refreshes folders
                this.local_folders = newFolders || [];
            },
            deep: true
        },
    },

}
</script>

<style scoped>
.sortable-ghost {
    opacity: 0.5;
    background: #f3f4f6 !important;
}

.sortable-chosen {
    background: #dbeafe !important;
    border-color: #3b82f6 !important;
}

.sortable-drag {
    opacity: 0.8;
}

.sortable-fallback {
    opacity: 0.8;
    background: #dbeafe !important;
    border: 2px solid #3b82f6 !important;
    border-radius: 4px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Ensure drag handle shows proper cursor */
.folder-drag-header {
    cursor: move;
}

/* Override any conflicting cursor styles during drag */
.sortable-chosen .folder-drag-header {
    cursor: move !important;
}
</style>
